/**
 * Shared utilities for renderer components
 */

const CLARITY_ORDER: readonly string[] = ['complaint', 'cause', 'correction'];

/**
 * Normalize a single request: sort actions keys alphabetically and, for 3Cs_text_analysis, order clarity keys as complaint, cause, correction.
 */
function normalizeRequest(request: any): any {
    const actions = request?.actions;
    if (!actions || typeof actions !== 'object') {
        return request;
    }
    const sortedActionKeys = Object.keys(actions).sort((a, b) => a.localeCompare(b));
    const newActions: Record<string, any> = {};
    for (const key of sortedActionKeys) {
        let value = actions[key];
        if (key === '3Cs_text_analysis' && value?.explanation?.clarity && typeof value.explanation.clarity === 'object') {
            const clarity = value.explanation.clarity;
            const newClarity: Record<string, any> = {};
            for (const k of CLARITY_ORDER) {
                if (clarity[k] !== undefined) {
                    newClarity[k] = clarity[k];
                }
            }
            const otherKeys = Object.keys(clarity).filter((k) => !CLARITY_ORDER.includes(k)).sort((a, b) => a.localeCompare(b));
            for (const k of otherKeys) {
                newClarity[k] = clarity[k];
            }
            value = { ...value, explanation: { ...value.explanation, clarity: newClarity } };
        }
        newActions[key] = value;
    }
    return { ...request, actions: newActions };
}

/**
 * Normalize action_output: for each agent, normalize each request's actions (alphabetical) and 3Cs_text_analysis.explanation.clarity (complaint, cause, correction).
 */
function normalizeRequestOrders(data: any): any {
    if (!data?.action_output || typeof data.action_output !== 'object') {
        return data;
    }
    const actionOutput = data.action_output;
    const newActionOutput: Record<string, any> = {};
    for (const agentName of Object.keys(actionOutput)) {
        const val = actionOutput[agentName];
        if (Array.isArray(val)) {
            newActionOutput[agentName] = val.map((item: any) => {
                if (!Array.isArray(item?.requests)) return item;
                return { ...item, requests: item.requests.map(normalizeRequest) };
            });
        } else if (val && typeof val === 'object' && Array.isArray(val.requests)) {
            newActionOutput[agentName] = { ...val, requests: val.requests.map(normalizeRequest) };
        } else {
            newActionOutput[agentName] = val;
        }
    }
    return { ...data, action_output: newActionOutput };
}

/**
 * Process error body - handles both string and object error bodies
 */
export const processErrorBody = (body: any) => {
    if (!body) return null;
    if (typeof body === 'string') {
        try {
            return JSON.parse(body);
        } catch {
            return null;
        }
    }
    return typeof body === 'object' ? body : null;
};

/**
 * `vehicle_owner_history.explanation` is usually an object map; some APIs / storage round-trips use a JSON string.
 * Arrays are not valid VHR explanation maps for accordion-style rendering.
 */
export const normalizeVhrExplanation = (raw: unknown): Record<string, unknown> | null => {
    if (raw == null) {
        return null;
    }
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw) as unknown;
            if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed as Record<string, unknown>;
            }
        } catch {
            return null;
        }
        return null;
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        return raw as Record<string, unknown>;
    }
    return null;
};

/**
 * Resolve `action_output[agentName]` when the API key does not exactly match `agent_name`
 * (trim/case drift, or a single Carfax/VHR bucket key).
 */
export const resolveActionOutputForAgent = (
    actionOutput: Record<string, unknown> | null | undefined,
    agentName: string
): unknown => {
    if (!actionOutput || typeof actionOutput !== 'object' || !agentName) {
        return undefined;
    }
    const ao: Record<string, unknown> = actionOutput;
    const exact = ao[agentName];
    if (exact !== undefined && exact !== null) {
        return exact;
    }
    const trimmed = agentName.trim();
    const keys = Object.keys(ao);
    const lowerAgent = trimmed.toLowerCase();

    const byTrim = keys.find((k) => k.trim() === trimmed);
    if (byTrim) {
        return ao[byTrim];
    }
    const byCase = keys.find((k) => k.toLowerCase() === lowerAgent);
    if (byCase) {
        return ao[byCase];
    }
    if (lowerAgent.includes('vhr') || lowerAgent.includes('carfax')) {
        const vhrKey = keys.find((k) => {
            const lk = k.toLowerCase();
            return lk.includes('vhr') || lk.includes('carfax');
        });
        if (vhrKey) {
            return ao[vhrKey];
        }
    }
    return undefined;
};

function pickReceivedUniversalClaimFromArray(claimScoreData: any[]): Record<string, any> | null {
    for (const item of claimScoreData) {
        if (item?.received_universal_claim_obj) {
            return item.received_universal_claim_obj;
        }
    }
    return null;
}

function pickJsonSourceOrSourceDataFromArray(claimScoreData: any[]): Record<string, any> | null {
    for (const item of claimScoreData) {
        if (item?.source_data?.json_sources?.[0]) {
            return item.source_data.json_sources[0];
        }
        if (item?.source_data) {
            return item.source_data;
        }
    }
    return null;
}

function buildVhrTemplatePayload(
    claimScoreData: any[],
    agentName: string,
    entityNumber: string | undefined
): Record<string, unknown> | null {
    const nameLower = (agentName || '').toLowerCase();
    const isLikelyVhrAgent = nameLower.includes('vhr') || nameLower.includes('carfax');
    const vhrPayload =
        claimScoreData.find((item: any) => item?.vehicle_owner_history) ??
        (isLikelyVhrAgent ? claimScoreData.find((item: any) => typeof item?.vin === 'string' && item?.source_data) : undefined);
    if (!vhrPayload?.vin) {
        return null;
    }
    const json0 = vhrPayload?.source_data?.json_sources?.[0];
    const base = typeof json0 === 'object' && json0 !== null ? (json0 as Record<string, unknown>) : {};
    return {
        ...base,
        vin: vhrPayload.vin,
        claim_no: entityNumber ?? base.claim_no,
        claim_id: entityNumber ?? base.claim_id,
    };
}

/**
 * Build `receivedClaimData` for ClaimScoreGeneratorRenderer from `claimScoreData`
 * (Claim Score array vs Carfax/VHR array with `vehicle_owner_history`).
 */
function asActionOutputRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function findClaimIntakeObject(actionOutput: unknown): Record<string, unknown> | null {
    const record = asActionOutputRecord(actionOutput);
    if (!record) return null;
    const key = Object.keys(record).find((k) => k.trim().toLowerCase() === 'claim intake object');
    if (!key) return null;
    return asActionOutputRecord(record[key]);
}

function extractMappedOutputFromBranch(branch: unknown): Record<string, unknown> | null {
    if (!branch || typeof branch !== 'object' || Array.isArray(branch)) return null;
    const data = (branch as Record<string, unknown>).data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const mappedOutput = (data as Record<string, unknown>).mapped_output;
        if (mappedOutput && typeof mappedOutput === 'object' && !Array.isArray(mappedOutput)) {
            return mappedOutput as Record<string, unknown>;
        }
    }
    return null;
}

/**
 * Extract mapped claim payload for UXGenie templates from claim intake `action_output`.
 * Mirrors ClaimIntakeAgentRenderer Sources-tab / template preview resolution.
 */
export const extractClaimIntakeData = (actionOutput: unknown): Record<string, unknown> | null => {
    const obj = findClaimIntakeObject(actionOutput);
    if (!obj) return null;

    return (
        extractMappedOutputFromBranch(obj.mapped_object) ??
        extractMappedOutputFromBranch(obj.claim_object) ??
        extractMappedOutputFromBranch(obj)
    );
};

export const extractReceivedClaimDataFromClaimScoreData = (
    claimScoreData: any,
    agentName: string,
    entityNumber: string | undefined
): Record<string, any> | null => {
    if (!claimScoreData) {
        return null;
    }

    if (Array.isArray(claimScoreData)) {
        const universal = pickReceivedUniversalClaimFromArray(claimScoreData);
        if (universal) {
            return universal;
        }
        const vhrPayload = buildVhrTemplatePayload(claimScoreData, agentName, entityNumber);
        if (vhrPayload) {
            return vhrPayload;
        }
        return pickJsonSourceOrSourceDataFromArray(claimScoreData);
    }

    return (
        claimScoreData?.received_universal_claim_obj ||
        claimScoreData?.source_data?.json_sources?.[0] ||
        claimScoreData?.source_data ||
        null
    );
};

/**
 * Extract common data from claimData
 */
export const extractRendererData = (claimData: any) => {
    let data;
    if (!claimData || (Array.isArray(claimData) && claimData.length === 0)) {
        data = null;
    } else if (Array.isArray(claimData)) {
        data = claimData[0];
    } else {
        data = claimData;
    }

    if (!data) {
        return {
            data: null,
            agentScore: 0,
            duration: 0,
            entityNumber: 'Unknown',
            timestamp: '',
            aiSummary: '',
            agentName: '',
            actionName: ''
        };
    }

    const normalizedData = normalizeRequestOrders(data);

    return {
        data: normalizedData,
        agentScore: normalizedData?.agent_score ?? 0,
        duration: normalizedData?.duration_ms ?? 0,
        entityNumber: normalizedData?.entity_number || 'Unknown',
        timestamp: normalizedData?.timestamp || '',
        aiSummary: normalizedData?.ai_summary || '',
        agentName: normalizedData?.agent_name || normalizedData?.input_data?.aiworker_name || normalizedData?.aiworker_name || '',
        actionName: normalizedData?.action_name || normalizedData?.name || ''
    };
};

